import React, { useState, useContext } from 'react';
import {
  NavLink
} from "react-router-dom";
import { withTheme } from '@emotion/react'
import styled from '@emotion/styled';
import mobileDetector from '../../helpers/mobileDetector';
import NavMenu from './NavMenu';
import logo from '../../assets/images/logo-study_kik.svg';
import close from '../../assets/images/nav_close.svg';
import open from '../../assets/images/nav_open.svg';
import EnvContext from '../../EnvContext';

const NAV_HEIGHT = '54px';

const Nav = styled.nav`
  color: ${props => props.theme.colors.neutral1};
  display: ${props => !!props.active ? `flex` : `none`};
  flex-basis: 100%;
  flex-direction: column;
  font-size: 24px;
  font-weight: bold;
  justify-content: space-between;
  left: 0;
  position: absolute;
  top: 61px;
  width: 100%;
  z-index: 9001;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    flex-direction: row;
    position: static;
    width: auto;
  }
`;

const StyledLink = styled(NavLink)`
  color: ${props => props.theme.colors.neutral1};
  display: flex;
  flex-basis: 100%;
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  justify-content: space-between;
  padding: 0 0 0 ${props => props.theme.margins.std};
  text-decoration: none;

  &:visited {
    color: ${props => props.theme.colors.nuetral1}; 
  }

  &.active {
    color: ${props => props.theme.colors.primary2};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 ${props => props.theme.margins.std} 0 0;
  }

  img {
    height: 48px;
    width: auto;
  }
`;

const AboutLink = styled(NavLink)`
  color: ${props => props.theme.colors.neutral1};
  display: flex;
  flex-basis: 100%;
  font-family: ${props => props.theme.fonts.primary};
  font-size: ${props => props.theme.fontSizes.sm};
  justify-content: space-between;
  padding: 0 0 0 ${props => props.theme.margins.std};
  text-decoration: none;

  &:hover {
    color: ${props => props.theme.colors.primary2}; 
  }

  &:visited {
    color: ${props => props.theme.colors.nuetral1}; 
  }

  &.active {
    color: ${props => props.theme.colors.primary2};
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    padding: 0 ${props => props.theme.margins.std} 0 0;
  }
`;

const NavListLinks = styled.ul`
  display: flex;
  flex-direction: column;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: row;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  margin-left: -${props => props.theme.margins.std};
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    margin-left: ${props => props.theme.margins.std};
    overflow-y: unset;
  }
`;

const Header = styled.header`
  align-items: center;
  background: ${props => props.theme.colors.contrast2};
  box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.06);
  display: flex;
  height: 62px;
  justify-content: center;
  position: fixed;
  top: 0;
  width: 100%;
  transform: translate3d(0, 0, 0);
  z-index: 9002;
`;

const HeaderContainer = styled.div`
  align-items: center;
  display: flex;
  flex-basis: 100%;
  max-width: ${props => props.theme.breakpoints.xxl};
  padding: 0 ${props => props.theme.margins.std};

  ${props => props.isMobile && "overflow-y: scroll;"}

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    overflow-y: unset;
  }
`;

const MenuIcon = styled.div`
  background-image: url(${props => !!props.active ? close : open});
  background-repeat: no-repeat;
  cursor: pointer;
  height: 24px;
  position: absolute;
  right: ${props => props.theme.margins.std};
  width: 24px;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavListLinkItem = styled.li`
  align-items: center;
  background-color: ${props => props.theme.colors.contrast2};
  border-bottom: 1px solid ${props => props.theme.colors.neutral3};
  display: flex;
  height: ${NAV_HEIGHT};
  position: relative;

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    border-bottom: none;
    margin-right: ${props => props.theme.margins.xxxxs};
  }
`;

const ScrollWrapper = styled.div`
  height: ${props => props.screenHeight - 53}px;
  position: absolute;
  top: 0px;
  width: 100%;
  ${props => props.isMobile && "overflow-y: scroll;"}

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    display: flex;
    flex-direction: row;
    height: 54px;
    justify-content: space-between;
    overflow-y: unset;
    position: static; 
  }
`;

const LoginLink = styled.a`
  font-family: ${props => props.theme.fonts.primary};
  background: ${props => props.theme.colors.primary1};
  border: none;
  border-radius: 6px;
  color: ${props => props.theme.colors.contrast2};
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  outline: none;
  padding: 8px 12px;
  text-align: center;
  text-decoration: none;
  width: 100%;
  margin: 0 ${props => props.theme.margins.std};

  transition: all .25s ease;

  &:hover {
    background-color: ${props => props.theme.colors.secondary3};
  }

  &:focus {
    background-color: ${props => props.theme.colors.secondary4};
  }

  @media (min-width: ${props => props.theme.breakpoints.md}) {
    max-width: 343px;
    margin: 0 auto;
  }

  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    width: 70px;
    margin: 0;
  }
`;

function Navigation(props) {
  const { primary1, primary2, primary3 } = props.theme.colors;
  const [active, setActive] = useState(false);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const { envBaseUrl } = useContext(EnvContext);

  const isMobile = mobileDetector();

  function handleClick() {
    setScreenHeight(window.innerHeight);
    setActive(!active);
  }
  
  return (
      <Header>
        <HeaderContainer isMobile={isMobile}>
          <LogoContainer>
            <StyledLink to="/">
              <img alt="Study Kik logo" src={logo} />
            </StyledLink>
          </LogoContainer>
          <Nav active={active}>
            <ScrollWrapper isMobile={isMobile} screenHeight={screenHeight}>
              <NavListLinks>
                <NavMenu menu="patients" title="For Patients & Families" highlightColor={primary1} />
                <NavMenu menu="sites" title="For Sites" highlightColor={primary2} />
                <NavMenu menu="sponsors" title="For Sponsors" highlightColor={primary3} />
              </NavListLinks>
              <NavListLinks>
                <NavListLinkItem>
                  <AboutLink to="/about">About</AboutLink>
                </NavListLinkItem>
                <NavListLinkItem>
                  <AboutLink to="/contact">Contact</AboutLink>
                </NavListLinkItem>
                <NavListLinkItem>
                  <LoginLink href={`${envBaseUrl}/login`} target="_blank">Login</LoginLink>
                </NavListLinkItem>
              </NavListLinks>
            </ScrollWrapper>
          </Nav>
          <MenuIcon active={active} onClick={() => handleClick()}></MenuIcon>
        </HeaderContainer>
      </Header>
  );
}

export default withTheme(Navigation);